import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Newspaper, Plus, Trash2, Loader2, Pin, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Pembangunan", "Bantuan Sosial", "Kegiatan", "Darurat", "Umum"];
const CAT_COLOR = {
  Pembangunan: "bg-sky-100 text-sky-700",
  "Bantuan Sosial": "bg-emerald-100 text-emerald-700",
  Kegiatan: "bg-violet-100 text-violet-700",
  Darurat: "bg-red-100 text-red-700",
  Umum: "bg-slate-100 text-slate-700",
};
const EMPTY = { title: "", category: "Umum", content: "", cover_image: "", pinned: false };

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/news"); setItems(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...EMPTY, ...n }); setDialog(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) { toast.error("Judul dan isi wajib diisi"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/news/${editing.id}`, form); toast.success("Pengumuman diperbarui"); }
      else { await api.post("/news", form); toast.success("Pengumuman dipublikasikan"); }
      setDialog(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/news/${id}`); toast.success("Pengumuman dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={Newspaper} title="Warta & Pengumuman" subtitle="Publikasikan informasi dan kegiatan desa"
        action={
          <Button onClick={openAdd} data-testid="news-add-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
            <Plus size={18} className="mr-1.5" /> Buat Pengumuman
          </Button>
        }
      />

      {loading ? (
        <div className="p-16 grid place-items-center"><Loader2 className="animate-spin text-[#064E3B]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4"><Newspaper className="text-[#064E3B]" /></div>
          <p className="text-slate-500">Belum ada pengumuman.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-grid">
          {items.map((n, i) => (
            <motion.div
              key={n.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition-shadow"
              data-testid={`news-card-${n.id}`}
            >
              {n.cover_image ? (
                <img src={n.cover_image} alt={n.title} className="h-40 w-full object-cover" onError={(e) => (e.target.style.display = "none")} />
              ) : (
                <div className="h-40 w-full bg-gradient-to-br from-[#064E3B] to-[#059669] grid place-items-center"><Newspaper className="text-white/50" size={40} /></div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CAT_COLOR[n.category] || CAT_COLOR.Umum}`}>{n.category}</span>
                  {n.pinned && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><Pin size={11} /> Disematkan</span>}
                </div>
                <h3 className="font-heading font-bold text-slate-900 leading-snug">{n.title}</h3>
                <p className="text-sm text-slate-500 mt-2 line-clamp-3 flex-1">{n.content}</p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(n)} data-testid={`news-edit-${n.id}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#064E3B]"><Pencil size={15} /></button>
                    <button onClick={() => remove(n.id)} data-testid={`news-delete-${n.id}`} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto custom-scroll" data-testid="news-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Ubah Pengumuman" : "Buat Pengumuman"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Judul *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" className="h-11" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-11" data-testid="news-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Link Foto Sampul (opsional)</Label>
              <Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="news-cover-input" className="h-11" placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Isi Pengumuman *</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="news-content-input" rows={5} required />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-medium">Sematkan Pengumuman</div>
                <div className="text-xs text-slate-400">Tampilkan di urutan teratas</div>
              </div>
              <Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} data-testid="news-pinned-switch" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(false)} className="rounded-xl">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="news-save-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl">
                {saving && <Loader2 className="animate-spin mr-2" size={16} />} Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
